import { Button } from '@/components/ui/button';
import {
    ShieldCheck,
    Search,
    Layers,
    Compass,
    ArrowRight,
    Library,
    Scale
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import aiHero from '@/assets/ai-methodology-hero.png';
import { motion } from 'framer-motion';
import { useViewTransitionNavigate } from "@/lib/hooks/use-view-transition";

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
};

// const staggerContainer = {
//     initial: { opacity: 0 },
//     whileInView: { opacity: 1, transition: { staggerChildren: 0.1 } },
//     viewport: { once: true }
// };

export default function HowWeUseAIPage() {
    const [, navigate] = useViewTransitionNavigate();

    return (
        <div className="flex flex-col min-h-full bg-background text-foreground overflow-y-auto">
            {/* Header / Back Navigation */}
            <div className="p-6 border-b border-border/50 bg-background/50 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between">
                {/* <Button variant="ghost" size="sm" onClick={() => navigate('/app/field-guide')} className="group">
                    <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    Back to Field Guide
                </Button> */}
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Research Methodology</div>
            </div>

            <div className="flex-grow">
                {/* Hero Section */}
                <section className="relative py-20 px-6 overflow-hidden">
                    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <Badge variant="outline" className="text-primary border-primary/30 py-1">AI-Curated Excellence</Badge>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-light mb-6 tracking-tight">How We Use <span className="text-primary italic font-serif">AI Responsibly</span></h1>
                            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                                BibleGlen’s AI serves as a precision research tool, pre-processing vast libraries of scholarly data to help you uncover the layers of history, culture, and literary design behind every verse.
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            className="relative"
                        >
                            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full -z-1" />
                            <img
                                src={aiHero}
                                alt="AI Scholar Methodology"
                                className="rounded-2xl border border-border/50 shadow-2xl relative z-10 aspect-video object-cover"
                            />
                        </motion.div>
                    </div>
                </section>

                {/* The "Scholarly Framework" Section */}
                <section className="py-20 px-6 bg-muted/30 border-y border-border/50">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-light mb-4">The Scholarly Framework</h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                Unlike generic AI that "chats" based on internet gossip, BibleGlen's research assistant is constrained by a rigorous academic field manual.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[
                                {
                                    icon: Library,
                                    title: "Trusted Sources",
                                    desc: "Synthesizes data from academic lexicons (BDAG, HALOT) and standard commentaries (NICNT, Anchor)."
                                },
                                {
                                    icon: Search,
                                    title: "Scholar's Lens",
                                    desc: "Prioritizes linguistic accuracy and historical evidence over generic, surface-level summaries."
                                },
                                {
                                    icon: ShieldCheck,
                                    title: "Evidence-Based",
                                    desc: "Every claim follows a lineage of thought. If it's not in the scholarly record, it doesn't make the cut."
                                },
                                {
                                    icon: Layers,
                                    title: "Structured Discovery",
                                    desc: "Findings are organized into Maps, Timelines, and Artifact Cards—not just blocks of text."
                                }
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    variants={fadeInUp}
                                    initial="initial"
                                    whileInView="whileInView"
                                    className="bg-background p-6 rounded-xl border border-border/50 shadow-sm flex flex-col items-center text-center"
                                >
                                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                        <item.icon className="text-primary w-5 h-5" />
                                    </div>
                                    <h3 className="font-semibold mb-2">{item.title}</h3>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why AI? & Theological Compass */}
                <section className="py-20 px-6">
                    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
                        <motion.div {...fadeInUp}>
                            <h3 className="text-2xl font-light mb-6 flex items-center gap-3">
                                <Scale className="text-primary w-6 h-6" />
                                Why We Use AI
                            </h3>
                            <div className="space-y-4 text-muted-foreground leading-relaxed">
                                <p>
                                    Meaningful biblical study shouldn't be gated behind a $2,000 library or a decade of graduate school. To provide this depth for every verse would normally require an army of scholars working for years.
                                </p>
                                <p>
                                    AI allows us to <span className="text-foreground font-medium">democratize the discovery process</span>. It is a high-performance tool—much like a precision laser scanner at a dig site—that helps you uncover layers of reality once hidden by language and time.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div {...fadeInUp}>
                            <h3 className="text-2xl font-light mb-6 flex items-center gap-3">
                                <Compass className="text-primary w-6 h-6" />
                                Our Theological Compass
                            </h3>
                            <div className="space-y-6">
                                <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
                                    <h4 className="font-semibold text-foreground mb-1">Respecting the Text</h4>
                                    <p className="text-sm text-muted-foreground italic">
                                        We believe the Bible is the inspired, authoritative Word of God. Our AI is prompted to prioritize historically orthodox interpretations that affirm its redemptive message.
                                    </p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
                                    <h4 className="font-semibold text-foreground mb-1">The Unity of Truth</h4>
                                    <p className="text-sm text-muted-foreground italic">
                                        We welcome insights from archaeology, linguistics, and history, viewing all data through a lens of faithful, historical orthodoxy.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Verification Section */}
                <section className="py-20 px-6 bg-slate-900/40 border-t border-border/50">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-light mb-12">Empowering Your Discernment</h2>
                        <div className="grid gap-6">
                            {[
                                { title: "Check the Sources", desc: "Every discovery card includes a 'Sources' list. We encourage you to look them up yourself." },
                                { title: "Compare the Work", desc: "Cross-reference our findings with your own trusted commentaries and study tools." },
                                { title: "Help Us Refine", desc: "If you spot an error, report it. Each report helps the research assistant become sharper." }
                            ].map((item, i) => (
                                <div key={i} className="flex items-start gap-4 text-left p-6 rounded-2xl bg-background/50 border border-border/50 group hover:border-primary/30 transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-sm">
                                        {i + 1}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{item.title}</h4>
                                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Bottom CTA */}
                <section className="py-20 px-6 text-center border-t border-border/50">
                    <div className="max-w-2xl mx-auto">
                        <p className="text-xl text-muted-foreground italic mb-10 leading-relaxed">
                            "The AI isn't here to do the thinking for you. It’s here to hand you the shovel and clear away the dust of 2,000 years. The discovery itself is yours to make."
                        </p>
                        <Button size="lg" className="rounded-full px-12 transition-transform hover:scale-105" onClick={() => navigate('/app/read')}>
                            Start Your Discovery <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    );
}

